import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import FootAnkel from '../images/Foot&Ankle.jpg'

export default function FootAnklePage() {
  const benefits = [
    "Comprehensive gait and biomechanical assessment",
    "Advanced imaging for accurate diagnosis",
    "Conservative management options",
    "Minimally invasive surgical techniques",
    "Specialized rehabilitation protocols",
    "Custom orthotic recommendations",
  ];

  const conditions = [
    {
      title: "Common Conditions",
      items: [
        "Ankle sprains and instability",
        "Achilles tendon injuries",
        "Plantar fasciitis",
        "Ankle fractures",
        "Bunions and toe deformities",
        "Flat foot and high arch",
      ],
    },
    {
      title: "Surgical Procedures",
      items: [
        "Ankle arthroscopy",
        "Ligament reconstruction",
        "Achilles tendon repair",
        "Fracture fixation (ORIF)",
        "Bunion correction",
        "Fusion procedures",
      ],
    },
  ];

  const faqs = [
    {
      question: "How long does it take to recover from ankle ligament reconstruction?",
      answer:
        "Recovery typically takes 3-6 months. Initial immobilization lasts 2-3 weeks, followed by progressive weight-bearing and physiotherapy. Return to sport usually occurs at 4-6 months after achieving full strength and stability.",
    },
    {
      question: "Can plantar fasciitis be treated without surgery?",
      answer:
        "Yes, over 90% of plantar fasciitis cases respond to conservative treatment including stretching exercises, orthotic devices, activity modification, and anti-inflammatory measures. Surgery is rarely needed.",
    },
    {
      question: "What is the success rate of Achilles tendon repair?",
      answer:
        "Modern surgical techniques have high success rates of 85-95%. Early surgical repair typically provides better outcomes than delayed treatment, especially for complete ruptures in active individuals.",
    },
    {
      question: "How soon can I walk after foot or ankle surgery?",
      answer:
        "This varies by procedure. Some surgeries allow immediate weight-bearing in a boot, while others require 6-8 weeks of non-weight-bearing. Your surgeon will provide specific guidelines based on your procedure.",
    },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary to-primary/90 text-primary-foreground py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="max-w-2xl">
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl mb-4">
              Foot & Ankle Care
            </h1>
            <p className="text-lg text-primary-foreground/90 leading-relaxed">
              Comprehensive diagnosis and treatment for all foot and ankle conditions
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 items-start">
            {/* Left Content */}
            <div>
              <h2 className="text-3xl font-bold mb-6">Expert Foot & Ankle Treatment</h2>
              <div className="prose prose-lg max-w-none space-y-4">
                <p>
                  The foot and ankle are complex structures containing 26 bones, 33 joints, and over 100 muscles,
                  tendons, and ligaments. Injuries and conditions affecting these structures can significantly impact
                  mobility and quality of life.
                </p>
                <p>
                  We provide comprehensive care from acute ankle sprains to chronic conditions like plantar fasciitis,
                  Achilles tendinopathy, and arthritic changes. Our approach combines thorough biomechanical assessment
                  with evidence-based treatment protocols.
                </p>
                <p>
                  Whether you need conservative management or surgical intervention, our goal is to restore function and
                  help you return to your activities with confidence.
                </p>
              </div>

              <div className="mt-8">
                <h3 className="text-xl font-semibold mb-4">Our Approach</h3>
                <ul className="space-y-3">
                  {benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-primary mt-0.5" />
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Right Image */}
            <div className="relative h-[500px] rounded-lg overflow-hidden shadow-xl">
              <img
                src= {FootAnkel}
                alt="Foot and ankle examination"
                className="object-cover w-full h-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Conditions */}
      <section className="py-16 bg-muted/30">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">Conditions We Treat</h2>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 max-w-4xl mx-auto">
            {conditions.map((condition) => (
              <Card key={condition.title}>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-4">{condition.title}</h3>
                  <ul className="space-y-2">
                    {condition.items.map((item, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <CheckCircle2 className="h-5 w-5 text-primary mt-0.5" />
                        <span className="text-muted-foreground">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Treatment Process */}
      <section className="py-16">
        <div className="mx-auto max-w-4xl px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">Treatment Process</h2>

          <div className="space-y-6">
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-3">Initial Assessment</h3>
                <p className="text-muted-foreground">
                  Comprehensive evaluation including clinical examination, gait analysis, and imaging (X-ray,
                  ultrasound, or MRI as needed).
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-3">Conservative Management</h3>
                <p className="text-muted-foreground">
                  Most foot and ankle conditions respond well to non-surgical treatment including physiotherapy, custom
                  orthotics, bracing, and targeted exercises.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-3">Surgical Intervention</h3>
                <p className="text-muted-foreground">
                  When surgery is needed, we use minimally invasive techniques including arthroscopy and percutaneous
                  methods.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-3">Rehabilitation</h3>
                <p className="text-muted-foreground">
                  Structured rehabilitation focuses on restoring range of motion, strength, and functional mobility.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-muted/30">
        <div className="mx-auto max-w-4xl px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">Frequently Asked Questions</h2>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`}>
                <AccordionTrigger className="text-left">{faq.question}</AccordionTrigger>
                <AccordionContent>{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Get Expert Foot & Ankle Care</h2>
          <p className="text-lg mb-8 text-muted-foreground max-w-2xl mx-auto">
            Schedule a consultation to discuss your foot or ankle condition and explore treatment options.
          </p>

          <Button size="lg" asChild>
            <Link to="/book-appointment">
              Book Appointment
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
